import React, { useState } from "react";
import { Box, FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, Grid, Typography, useMediaQuery } from "@mui/material";
import ShowChartIcon from '@mui/icons-material/ShowChart'; // Icon for technical indicators
import IndicatorVideo from "./IndicatorBackground.mp4"; // Example video for background, adjust path if necessary

const TechnicalIndicatorSection = ({ onIndicatorChange }) => {
  const isMobile = useMediaQuery("(max-width:600px)");

  const [selectedDailyIndicators, setSelectedDailyIndicators] = useState([]); // Daily indicator selection
  const [selectedHourlyIndicators, setSelectedHourlyIndicators] = useState([]); // Hourly indicator selection

  const options = [
    "ATR increasing",
    "BBW increasing",
    "OBV increasing",
    "CMF positive and increasing",
    "Stochastic Oscillator: Oversold",
    "Price above SMA_50",
    "SMA_50 above SMA_200",
    "Price above EMA_50",
    "RSI below 30",
    "MACD line above Signal line",
    "Price below Lower Bollinger Band",
    "Price above SAR",
  ];

  // Handle change for hourly indicators
  const handleHourlyChange = (event) => {
    setSelectedHourlyIndicators(event.target.value);
    onIndicatorChange("hourly", event.target.value); // Notify parent component
  };

  // Handle change for daily indicators
  const handleDailyChange = (event) => {
    setSelectedDailyIndicators(event.target.value);
    onIndicatorChange("daily", event.target.value); // Notify parent component
  };

  return (
    <Box
      sx={{
        position: "relative",
        
        minHeight: "10vh",
        overflow: "hidden",
        backgroundColor: "#000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        padding: "20px",
        gap: "16px",
      }}
    >
      {/* Background Video */}
      <video
        autoPlay
        loop
        muted
        playsInline
        style={{
          position: "absolute",
          top: "0",
          left: "0",
          width: "100%",
          height: "100%",
          objectFit: "cover",
          opacity: 0.7,
        }}
      >
        <source src={IndicatorVideo} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Overlay */}
      <Box
        sx={{
          position: "absolute",
          top: "0",
          left: "0",
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.6)",
        }}
      />

      {/* Title */}
      <Typography
        variant="h4"
        sx={{
          zIndex: 1,
          color: "#fff",
          fontWeight: "bold",
          fontFamily: "'Work Sans', sans-serif",
          letterSpacing: "2px",
          marginBottom: "16px",
        }}
      >
        <ShowChartIcon
          sx={{
            fontSize: "2rem",
            verticalAlign: "middle",
            marginRight: "8px",
          }}
        />
        Technical Indicators
      </Typography>

      {/* Indicators Grid */}
      <Grid
        container
        spacing={3}
        justifyContent="center"
        sx={{
          zIndex: 1,
          maxWidth: isMobile ? "100%" : "80%",
        }}
      >
        {/* Hourly Indicators */}
        <Grid item xs={11} sm={6}>
          <FormControl fullWidth sx={{ backgroundColor: "rgba(255, 255, 255, 0.1)", borderRadius: "8px" }}>
            <InputLabel id="hourly-indicator-label" sx={{ color: "white" }}>Hourly Indicator</InputLabel>
            <Select
              labelId="hourly-indicator-label"
              multiple
              value={selectedHourlyIndicators}
              onChange={handleHourlyChange}
              renderValue={(selected) => selected.join(", ")}
              sx={{
                backgroundColor: "transparent",
                color: "white",
                "& .MuiInputBase-root": {
                  fontFamily: "'Work Sans', sans-serif",
                  color: "white",
                },
                "& .MuiInputLabel-root": {
                  color: "lightgray",
                  fontFamily: "'Work Sans', sans-serif",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "transparent",
                  },
                  "&:hover fieldset": {
                    borderColor: "lightblue",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "lightblue",
                  },
                },
              }}
            >
              {options.map((option) => (
                <MenuItem key={option} value={option}>
                  <Checkbox checked={selectedHourlyIndicators.indexOf(option) > -1} />
                  <ListItemText primary={option} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Daily Indicators */}
        <Grid item xs={11} sm={6}>
          <FormControl fullWidth sx={{ backgroundColor: "rgba(255, 255, 255, 0.1)", borderRadius: "8px" }}>
            <InputLabel id="daily-indicator-label" sx={{ color: "white" }}>Daily Indicator</InputLabel>
            <Select
              labelId="daily-indicator-label"
              multiple
              value={selectedDailyIndicators}
              onChange={handleDailyChange}
              renderValue={(selected) => selected.join(", ")}
              sx={{
                backgroundColor: "transparent",
                color: "white",
                "& .MuiInputBase-root": {
                  fontFamily: "'Work Sans', sans-serif",
                  color: "white",
                },
                "& .MuiInputLabel-root": {
                  color: "lightgray",
                  fontFamily: "'Work Sans', sans-serif",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "transparent",
                  },
                  "&:hover fieldset": {
                    borderColor: "lightblue",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "lightblue",
                  },
                },
              }}
            >
              {options.map((option) => (
                <MenuItem key={option} value={option}>
                  <Checkbox checked={selectedDailyIndicators.indexOf(option) > -1} />
                  <ListItemText primary={option} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TechnicalIndicatorSection;
